/*
 * Copyright (c) CIRROM 2026.
 */

/**
 * Resolves a backend `system_type` to the CRM vendor behind it.
 *
 * The tenant names its own systems, and those names are rarely the vendor's -
 * the demo tenant's HubSpot system is called "Cirrom" - so anything that wants
 * to say "Open in HubSpot" has to read the type, never the name.
 */

export interface CrmVendor {
  /** Vendor name as it should appear in a sentence: "Open in HubSpot". */
  label: string;
  /** Compact mark under public/logos/icon, sized for a 16px row. */
  iconSrc: string;
}

/*
 * Matched as substrings of the type with its separators stripped, because the
 * enum spells one vendor several ways: DEAL_CLOUD, VUTURE_IN_DEALCLOUD,
 * REL_INTEL_DEAL_CLOUD. Order is load-bearing - a HubSpot system carrying
 * InterAction reference data is typed HUBSPOT_WITH_INTERACTION_REFDATA, and
 * the system it opens is HubSpot, so HubSpot has to be asked first.
 */
const VENDORS: [token: string, vendor: CrmVendor][] = [
  ["HUBSPOT", { label: "HubSpot", iconSrc: "/logos/icon/hubspot.svg" }],
  [
    "SALESFORCE",
    { label: "Salesforce", iconSrc: "/logos/icon/salesforce.svg" },
  ],
  [
    "DYNAMICS",
    {
      label: "Microsoft Dynamics",
      iconSrc: "/logos/icon/microsoftdynamics.svg",
    },
  ],
  ["DEALCLOUD", { label: "DealCloud", iconSrc: "/logos/icon/dealcloud.svg" }],
  [
    "INTERACTION",
    { label: "InterAction", iconSrc: "/logos/icon/interaction.svg" },
  ],
];

/**
 * The vendor for a `system_type`, or null for the file and generic types
 * (CSV, EXCEL, CRM, anything unrecognised) that have no mark of their own -
 * callers fall back to their neutral wording there.
 */
export const resolveCrmVendor = (
  systemType?: string | null
): CrmVendor | null => {
  if (!systemType) {
    return null;
  }
  const normalised = systemType.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const match = VENDORS.find(([token]) => normalised.includes(token));
  return match ? match[1] : null;
};
